/**
 * publicPlansService.js
 * =====================
 * Cliente do GET /api/billing/public/plans — catálogo público da vitrine.
 * READ-ONLY. Sem auth (endpoint público).
 *
 * Modelo comercial (07/07/2026 — híbrido "grade + à la carte"):
 *   - Grade = verdade: 16 células (basic/basic_plus/pro/elite × S/M/L/XL),
 *     preço por célula em price_brl_cents.
 *   - Extras por cima: price_per_athlete_cents / price_per_staff_cents são
 *     o preço MARGINAL do extra (não componente do base).
 *   - Gate dos extras: role_seats (coach/prep/nutritionist/manager_seats)
 *     vindos das quota_policies. limit 0 = plano NÃO inclui o papel →
 *     stepper daquele papel NÃO renderiza. A regra vem do banco, nunca
 *     hardcoded aqui.
 *
 * Cache 5 min: catálogo muda por migração, não por request.
 */
import { AZURE } from './api';

let _cache = null;
let _cacheTs = null;
const CACHE_TTL_MS = 5 * 60 * 1000;

/**
 * Busca catálogo público (16 células + role_seats + preços marginais).
 * @returns {Promise<Array<{
 *   plan_key, plan_name, plan_family, size_band, tier_order,
 *   price_brl_cents, annual_price_cents, athlete_limit, ai_credits_monthly,
 *   storage_mb, price_per_athlete_cents, price_per_staff_cents,
 *   role_seats: {coach_seats, prep_seats, nutritionist_seats, manager_seats}
 * }>>}
 */
export async function getPublicPlans({ force = false } = {}) {
  const now = Date.now();
  if (!force && _cache && _cacheTs && (now - _cacheTs) < CACHE_TTL_MS) {
    return _cache;
  }
  const { data } = await AZURE.get('/billing/public/plans');
  if (!data?.success) throw new Error(data?.error || 'Falha ao buscar catálogo');
  _cache = data.data.plans || [];
  _cacheTs = now;
  return _cache;
}

/** R$ centavos → "R$ 1.990" (sem decimais — preços são inteiros). */
export function formatBRL(cents) {
  if (cents == null) return null;
  return `R$ ${Math.round(cents / 100).toLocaleString('pt-BR')}`;
}

/**
 * Preço total = célula + extras à la carte.
 * @param {object} plan célula selecionada (do catálogo)
 * @param {object} extras {athletePacks: n (packs de +5), staff: n}
 */
export function computeTotal(plan, extras = {}) {
  const base = plan?.price_brl_cents || 0;
  const athleteCents = (extras.athletePacks || 0) * 5 * (plan?.price_per_athlete_cents || 0);
  const staffCents = (extras.staff || 0) * (plan?.price_per_staff_cents || 0);
  return base + athleteCents + staffCents;
}
